// src/components/SequenceEditor/Triggers/KeywordTrigger.jsx

import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Input, Button, Popover, Tag, Radio, message, Tooltip } from 'antd';
import { DownOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { cn } from 'helper/cn';
import Xmark from '../../../../../../assets/customSVG/Xmark';
import { updateInitialTrigger } from '../../../../api/sequence';
import { checkKeyword } from '../../../../api/sequence';
import { whatsappBusinessNumbers } from '../../../../../../api/Api';
import {
  useAspDispatch,
  useAspSelector,
} from '../../../../../../test/jest-redux-hooks';

// notification
import NotificationContext from '../../../../../../providers/NotificationProvider';
import {
  showSuccessToast,
  showErrorToast
} from '../../../../../../components/commonComponents/gxAntdToast/toastUtils';

// Feature Gating
import { useFunction } from '../../../../../../hooks/useFeatureGating';
import { FUNCTIONS } from '../../../../../../constant/featureSlugs';

export default function KeywordTrigger({
  node,
  originalData,
  onChange,
  toggleNodeEditMode,
  allowToEditFlow,
}) {
  const { campaign_id, sequence_id } = useParams();
  const { currentBrand } = useAspSelector((s) => s.app);
  
  const { limit } = useFunction(FUNCTIONS.SEQUENCE_KEYWORD_LIMIT);
  const keywordLimit = limit || 10;

  // states
  const [selectedNumbers, setSelectedNumbers] = useState(
    node.data.phone_numbers || []
  );
  const [keywords, setKeywords] = useState(node.data.keywords || []);
  const [newKeyword, setNewKeyword] = useState('');
  const [isAddingKeyword, setIsAddingKeyword] = useState(false);
  const keywordInputRef = useRef(null);

  const [matchType, setMatchType] = useState(node.data.match_type || 'exact');
  const [matchThreshold, setMatchThreshold] = useState(
    node.data.match_threshold || 80
  );

  const [list, setList] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState({ type: null, message: '' });

  const notification = useContext(NotificationContext);

  // const url = "https://api.whatsapp.com/send/?phone=#{phone_number}&text=#{URI.encode_uri_component trigger_text}&type=phone_number"

  // end drag on scroll
  useEffect(() => {
    const endDrag = () => window.dispatchEvent(new MouseEvent('mouseup'));
    window.addEventListener('scroll', endDrag, true);
    return () => window.removeEventListener('scroll', endDrag, true);
  }, []);

  // sync incoming props
  useEffect(() => {
    setSelectedNumbers(node.data.phone_numbers || []);
    setKeywords(node.data.keywords || []);
    setMatchType(node.data.match_type || 'exact');
    setMatchThreshold(node.data.match_threshold || 80);
  }, [node]);

  // fetch WhatsApp numbers
  useEffect(() => {
    if (!currentBrand) return;
    setIsLoading(true);
    whatsappBusinessNumbers(currentBrand.brand_id)
      .then((res) => setList(res.data.wapp_business_num || []))
      .catch(() =>
        showErrorToast(notification, 'Failed to load WhatsApp numbers')
      )
      .finally(() => setIsLoading(false));
  }, [currentBrand]);

  const formatNumber = (n) =>
    `+${n.country_code} ${n.send_from_phone_number}${
      n.send_from_phone_name ? ` (${n.send_from_phone_name})` : ''
    }`;

  // multi-select phone numbers
  const toggleNumber = (n) => {
    const exists = selectedNumbers.some((s) => s.id === n.id);
    const updated = exists
      ? selectedNumbers.filter((s) => s.id !== n.id)
      : [...selectedNumbers, n];
    setSelectedNumbers(updated);
    // onChange({ ...node, data: { ...node.data, phone_numbers: updated } });
  };

  const addKeyword = async () => {
    const kw = newKeyword.trim();
    setError({ type: null, message: '' }); // Reset error state

    if (!kw) return;
    if (keywords.length >= keywordLimit) {
      setError({
        type: 'warning',
        message: `Maximum limit of ${keywordLimit} keywords reached`,
      });
      return;
    }
    if (kw.length > 35) {
      setError({
        type: 'error',
        message: 'Keyword cannot exceed 35 characters',
      });
      return;
    }
    if (keywords.some((k) => k.toLowerCase() === kw.toLowerCase())) {
      setError({ type: 'error', message: 'This keyword already exists' });
      return;
    }

    setIsAddingKeyword(true);
    try {
      const response = await checkKeyword(currentBrand?.brand_id, {
        keyword: kw,
        match_type: matchType,
        threshold: matchType == 'exact' ? 100 : matchThreshold,
        sequence_id: sequence_id,
      });

      const {
        exists,
        exact_matches = [],
        fuzzy_matches = [],
        qr_keyword,
      } = response.data;

      // Check if keyword exists as a QR keyword
      if (qr_keyword) {
        setError({
          type: 'error',
          message: `This keyword is already used in a QR code with text: "${qr_keyword.settings.url_text}"`,
        });
        return;
      }

      // Check for exact matches
      if (exists || exact_matches.length > 0) {
        const exactKeywords = exact_matches
          .map((m) => `"${m.actual_keyword || m.keyword}"`)
          .join(', ');
        let message = `This keyword is similar to: ${exactKeywords || fuzzy_matches?.[0]?.actual_keyword}. `;
        message +=
          ' You can view and manage all keywords in Settings > Keywords.';

        // Include fuzzy matches in the error message if they exist
        if (fuzzy_matches.length > 0) {
          const similarKeywords = fuzzy_matches
            .map((m) => `"${m.actual_keyword || m.keyword}"`)
            .join(', ');
          message += ` This keyword is also similar to: ${similarKeywords}.`;
        }

        setError({
          type: 'error',
          message: (
            <span>
              {message.split('. ').map((part, i, arr) => (
                <React.Fragment key={i}>
                  {part}
                  {i < arr.length - 1 ? '. ' : ''}
                  {i === 0 && (
                    <a
                      href="/user/settings/keywords"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline ml-1"
                    >
                      View in Settings
                    </a>
                  )}
                </React.Fragment>
              ))}
            </span>
          ),
        });
        return;
      }

      // Check for fuzzy matches if in exact match mode
      if (matchType === 'exact' && fuzzy_matches.length > 0) {
        const similarKeywords = fuzzy_matches
          .map(
            (m) =>
              `"${m.actual_keyword || m.keyword}" (${Math.round(
                m.similarity * 100
              )}% match)`
          )
          .join(', ');
        setError({
          type: 'warning',
          message: `Similar keywords found: ${similarKeywords}. Consider using fuzzy match mode.`,
        });
        return;
      }

      // If validation passes, add the keyword
      const updated = [...keywords, kw];
      setKeywords(updated);
      setNewKeyword('');
      setError({ type: null, message: '' });
      
      // Focus the input after state updates
      setTimeout(() => {
        if (keywordInputRef.current) {
          const input = keywordInputRef.current.input;
          if (input) {
            input.focus();
          }
        }
      }, 0);
    } catch (error) {
      setError({
        type: 'error',
        message: error.response?.data?.message || 'Failed to validate keyword',
      });
    } finally {
      setIsAddingKeyword(false);
    }
  };

  const removeKeyword = (kw) => {
    const updated = keywords.filter((k) => k !== kw);
    setKeywords(updated);
    // onChange({ ...node, data: { ...node.data, keywords: updated } });
  };

  // save trigger
  const handleSave = async () => {
    if (!keywords.length) {
      return message.error('Please add at least one keyword');
    }
    setIsUpdating(true);
    try {
      const payload = {
        brand_id: currentBrand?.brand_id,
        campaign_id,
        trigger_type: 'KEYWORD_TRIGGER',
        save: true,
        details: {
          phone_numbers: selectedNumbers,
          keywords,
          match_type: matchType,
          match_threshold: matchThreshold,
          url: `https://api.whatsapp.com/send/?phone=${
            selectedNumbers?.[0]?.country_code +
            selectedNumbers?.[0]?.send_from_phone_number
          }`,
        },
        remove_edges: false,
      };

      const res = await updateInitialTrigger(sequence_id, node?.uniq_id, {
        ...payload,
        defaults: {
          ...node?.settings?.defaults,
          isEditMode: false,
        },
      });
      if (res?.status === 200) {
        // Create updated node with response data
        const responseData = res?.data?.data?.[0] || {};

        // Update node data in parent
        onChange({
          type: 'UPDATE_NODE_DATA',
          id: node?.uniq_id,
          data: responseData,
        });

        // Close the edit mode
        toggleNodeEditMode({
          id: node?.id,
          uniqId: node?.uniq_id,
          value: 'false',
          defaults: {
            ...node?.settings?.defaults,
            isEditMode: false,
          },
        });

        showSuccessToast(
          notification,
          'API Trigger configuration saved successfully'
        );
      } else {
        throw new Error(
          res?.response?.data?.error || 'Failed to save configuration'
        );
      }
    } catch (error) {
      console.error('Error saving API trigger configuration:', error);
      showErrorToast(
        notification,
        error.response?.data?.message ||
          error.message ||
          'Failed to save configuration'
      );
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="w-full bg-white rounded-lg shadow-lg overflow-hidden">
      {/* header */}
      <div
        className={cn(
          'flex items-center justify-between px-5 py-2.5 bg-[#04B8A2] text-white'
        )}
      >
        <div className="flex items-center gap-3">
          {/* <Icons keyProp={node.label.toLowerCase()} bgProp="transparent" /> */}
          <p className="capitalize font-[500]">{node.label}</p>
        </div>
        <div
          className="cursor-pointer"
          onClick={() =>
            toggleNodeEditMode({
              id: node.id,
              uniqId: node.uniq_id,
              value: 'false',
              defaults: node.settings?.defaults,
            })
          }
        >
          <Xmark color="white" width={12} height={12} />
        </div>
      </div>

      {/* body */}
      <div className="p-6 space-y-8 bg-white max-h-[calc(100vh-300px)] overflow-y-auto keyword-trigger-scroll">
        <style jsx>{`
          .keyword-trigger-scroll {
            overflow: hidden;
          }

          .keyword-trigger-scroll:hover {
            overflow: auto;
            scrollbar-width: thin;
            scrollbar-color: #0d9488 #e5e7eb;
          }

          .keyword-trigger-scroll:hover::-webkit-scrollbar {
            width: 8px;
            height: 8px;
          }

          .keyword-trigger-scroll:hover::-webkit-scrollbar-track {
            background: #e5e7eb;
            border-radius: 4px;
          }

          .keyword-trigger-scroll:hover::-webkit-scrollbar-thumb {
            background-color: #0d9488;
            border-radius: 4px;
          }

          .keyword-trigger-scroll:hover::-webkit-scrollbar-thumb:hover {
            background-color: #0f766e;
          }
        `}</style>

        {/* WhatsApp multi-select */}
        <div className="!mt-0">
          <div className="mb-4">
            <h5 className="text-sm font-[500] text-gray-800 mb-1">
              WhatsApp Number(s)
            </h5>
            <p className="text-xs text-gray-500">
              Select the numbers that will receive these keywords
            </p>
          </div>
          <Popover
            placement="bottom"
            overlayClassName="popover-arrow-center"
            content={
              <div className="max-h-60 overflow-y-auto p-1.5 space-y-1.5">
                {isLoading ? (
                  <p className="text-center text-gray-500 py-2">Loading…</p>
                ) : list.length ? (
                  list.map((n) => (
                    <div
                      key={n.id}
                      onClick={() => toggleNumber(n)}
                      className={cn(
                        'p-2.5 rounded-md cursor-pointer flex justify-between items-center text-sm transition-colors',
                        selectedNumbers.some((s) => s.id === n.id)
                          ? 'bg-teal-50 text-teal-800 font-[500]'
                          : 'text-gray-700 hover:bg-gray-100'
                      )}
                    >
                      <span className="truncate">{formatNumber(n)}</span>
                      {selectedNumbers.some((s) => s.id === n.id) && (
                        <span className="text-teal-500 ml-2">
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M20 6L9 17L4 12"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </span>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 p-2">No WhatsApp numbers found</p>
                )}
              </div>
            }
            trigger="click"
            open={isOpen}
            onOpenChange={setIsOpen}
          >
            <button
              className={`w-full border border-gray-200 rounded-lg px-4 py-2.5 flex flex-wrap gap-2 items-center text-left transition-colors ${
                !allowToEditFlow
                  ? 'bg-gray-100 cursor-not-allowed border-gray-200'
                  : 'bg-white hover:border-teal-300 focus-within:border-teal-400'
              }`}
              disabled={!allowToEditFlow}
            >
              {selectedNumbers.length ? (
                selectedNumbers.map((n) => (
                  <span
                    key={n.id}
                    className="bg-teal-50 text-teal-800 px-3 py-1 rounded-full text-sm font-[500] flex items-center gap-1.5"
                  >
                    {formatNumber(n)}
                    {allowToEditFlow && (
                      <span
                        className="text-teal-500 hover:text-teal-700"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleNumber(n);
                        }}
                      >
                        ×
                      </span>
                    )}
                  </span>
                ))
              ) : (
                <span
                  className={`${
                    !allowToEditFlow ? 'text-gray-400' : 'text-gray-500'
                  }`}
                >
                  Select WhatsApp Number(s)
                </span>
              )}
              <DownOutlined
                className={`ml-auto ${
                  !allowToEditFlow ? 'text-gray-400' : 'text-gray-500'
                }`}
              />
            </button>
          </Popover>
        </div>

        {/* Matching Method */}
        <div>
          <div className="mb-4">
            <div className="flex items-center mb-1.5">
              <h5 className="text-sm font-[500] text-gray-800 mr-2">
                Matching Method
              </h5>
              <Tooltip title="Choose how incoming text is matched">
                <QuestionCircleOutlined className="text-gray-400 text-sm" />
              </Tooltip>
            </div>
            <p className="text-xs text-gray-500 mb-3">
              Select how strictly keywords should be matched
            </p>
          </div>
          <div className="mt-1">
            <Radio.Group
              onChange={(e) => setMatchType(e.target.value)}
              value={matchType}
              disabled={!allowToEditFlow}
              className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full"
            >
              <Radio
                value="exact"
                className={`radio-custom ${
                  !allowToEditFlow ? 'opacity-50' : ''
                }`}
              >
                <span
                  className={
                    matchType === 'exact'
                      ? 'font-[500] text-teal-700'
                      : 'text-gray-700'
                  }
                >
                  Exact Match
                </span>
                <span className="block text-xs text-gray-500 mt-0.5">
                  Matches exact keywords only
                </span>
              </Radio>
              <Radio
                value="fuzzy"
                className={`radio-custom ${
                  !allowToEditFlow ? 'opacity-50' : ''
                }`}
              >
                <div>
                  <span
                    className={
                      matchType === 'fuzzy'
                        ? 'font-[500] text-teal-700'
                        : 'text-gray-700'
                    }
                  >
                    Fuzzy Match
                  </span>
                  <span className="block text-xs text-gray-500 mt-0.5">
                    Matches similar words and typos
                  </span>
                </div>
              </Radio>
            </Radio.Group>
          </div>

          {matchType === 'fuzzy' && (
            <div className="mt-6">
              <div className="flex justify-between items-center mb-3">
                <div>
                  <div className="text-sm font-[500] text-gray-800">
                    Match Threshold
                  </div>
                  <div className="text-xs text-gray-500">
                    Adjust matching sensitivity
                  </div>
                </div>
                <span className="font-[500] bg-teal-50 text-teal-700 px-2.5 py-1 rounded-md text-sm">
                  {matchThreshold}%
                </span>
              </div>
              <div className="relative w-full mb-1">
                <div className="relative w-full">
                  <input
                    type="range"
                    min="30"
                    max="100"
                    step="5"
                    value={matchThreshold}
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      setMatchThreshold(value);
                    }}
                    disabled={!allowToEditFlow}
                    className={`w-full h-1.5 rounded-full appearance-none ${
                      !allowToEditFlow
                        ? 'opacity-50 cursor-not-allowed'
                        : 'cursor-pointer'
                    }`}
                    style={{
                      background: `linear-gradient(to right, #0D9488 0%, #0D9488 ${
                        ((matchThreshold - 30) / 70) * 100
                      }%, #E5E7EB ${
                        ((matchThreshold - 30) / 70) * 100
                      }%, #E5E7EB 100%)`,
                      WebkitAppearance: 'none',
                      outline: 'none',
                      height: '6px',
                    }}
                  />
                  <style jsx global>{`
                    input[type='range']::-webkit-slider-thumb {
                      -webkit-appearance: none;
                      width: 18px;
                      height: 18px;
                      border-radius: 50%;
                      background: #0d9488;
                      cursor: pointer;
                      border: 2px solid white;
                      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                    }
                    input[type='range']:disabled::-webkit-slider-thumb {
                      background: #9ca3af;
                      cursor: not-allowed;
                    }
                  `}</style>
                </div>
              </div>
            </div>
          )}

          <style jsx global>{`
            .radio-custom .ant-radio-inner {
              border-color: #d1fae5;
              background-color: #f0fdfa;
            }
            .radio-custom .ant-radio-checked .ant-radio-inner {
              border-color: #0d9488;
              background-color: #f0fdfa;
            }
            .radio-custom .ant-radio-inner::after {
              background-color: #0d9488;
            }
            .radio-custom:hover .ant-radio-inner {
              border-color: #0d9488;
            }
          `}</style>
        </div>

        {/* Keywords */}
        <div>
          <div className="flex justify-between items-start mb-2">
            <div>
              <div className="flex items-center mb-1">
                <h5 className="text-sm font-[500] text-gray-800 mr-1.5">
                  Keywords
                </h5>
                <Tooltip title="Add words that will trigger this sequence">
                  <QuestionCircleOutlined className="text-gray-400 text-xs" />
                </Tooltip>
              </div>
              <p className="text-xs text-gray-500">
                Type and press Enter to add keywords
              </p>
            </div>
            <span className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded">
              {keywords.length}/{keywordLimit}
            </span>
          </div>
          <div className="flex gap-2 mb-3">
            <div className="relative flex-1">
              <Input
                ref={keywordInputRef}
                autoFocus
                placeholder={
                  keywords.length >= keywordLimit
                    ? `Maximum ${keywordLimit} keywords allowed`
                    : 'Type and press Enter to add keyword (max 35 chars)'
                }
                value={newKeyword}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && keywords.length < keywordLimit) {
                    e.preventDefault();
                    addKeyword();
                  }
                }}
                onChange={(e) => {
                  if (e.target.value.length <= 35) {
                    setNewKeyword(e.target.value);
                    // Clear error when user starts typing
                    if (error.message) setError({ type: null, message: '' });
                  }
                }}
                onPressEnter={(e) => {
                  e.preventDefault();
                  if (keywords.length < keywordLimit) {
                    addKeyword();
                  }
                }}
                disabled={
                  !allowToEditFlow || isAddingKeyword || keywords.length >= keywordLimit
                }
                className={`${
                  error.type === 'error'
                    ? 'border-red-500'
                    : error.type === 'warning'
                    ? 'border-amber-500'
                    : keywords.length >= keywordLimit
                    ? 'border-red-200'
                    : newKeyword.length >= 30
                    ? 'border-amber-200'
                    : 'hover:border-teal-300 focus:border-teal-400'
                }`}
                status={
                  error.type === 'error'
                    ? 'error'
                    : error.type === 'warning'
                    ? 'warning'
                    : keywords.length >= keywordLimit
                    ? 'error'
                    : newKeyword.length >= 30
                    ? 'warning'
                    : ''
                }
                maxLength={35}
                showCount={{
                  formatter: ({ count }) => (
                    <span className="text-xs text-gray-400">{count}/35</span>
                  ),
                }}
              />
            </div>
            <Button
              type="primary"
              loading={isAddingKeyword}
              onClick={addKeyword}
              disabled={
                !newKeyword.trim() || !allowToEditFlow || keywords.length >= keywordLimit
              }
              className={`h-10 ${
                newKeyword.trim() || !allowToEditFlow || keywords.length < keywordLimit
                  ? '!bg-teal-600 !text-white'
                  : '!bg-gray-200 !border-gray-200'
              }`}
            >
              Add
            </Button>
          </div>
          {error.message && (
            <div
              className={`mt-1 text-sm ${
                error.type === 'error'
                  ? 'text-red-600'
                  : error.type === 'warning'
                  ? 'text-amber-600'
                  : 'text-green-600'
              }`}
            >
              {error.message}
            </div>
          )}
          <div className="flex flex-wrap gap-2 min-h-[40px] items-start mt-1">
            {keywords.length ? (
              keywords.map((kw) => (
                <div
                  key={kw}
                  className="relative group bg-teal-50 border border-teal-100 rounded-lg px-3 py-1.5 pr-8 text-sm text-teal-800 font-[500] flex items-center transition-all hover:bg-teal-100 hover:border-teal-200 shadow-sm"
                >
                  {kw}
                  {allowToEditFlow && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeKeyword(kw);
                      }}
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center rounded-full text-teal-400 transition-colors"
                      aria-label={`Remove keyword ${kw}`}
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="opacity-70 group-hover:opacity-100"
                      >
                        <path
                          d="M18 6L6 18M6 6L18 18"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              ))
            ) : (
              <div className="text-gray-400 text-sm italic py-1">
                No keywords added yet. Start typing to add keywords.
              </div>
            )}
          </div>
          <div className="text-xs text-gray-500 mt-4 px-1">
            <h4 className="font-medium text-gray-700 mb-2 text-sm flex items-center">
              <span>Info</span>
              <svg
                className="w-3.5 h-3.5 ml-1 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </h4>
            <div className="flex flex-col space-y-1.5 pl-1">
              <div className="inline-flex items-center text-xs text-gray-500">
                Keywords are not case sensitive
              </div>
              <div className="inline-flex items-center text-xs text-gray-500">
                {/* <svg
                  className="w-3.5 h-3.5 mr-1.5 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg> */}
                Maximum 35 characters per keyword
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* footer */}
      <div className="flex justify-end items-center gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100">
        <Button
          type="primary"
          onClick={handleSave}
          loading={isUpdating}
          disabled={
            !allowToEditFlow || !keywords.length || selectedNumbers.length === 0
          }
          className={`h-9 px-6 ${
            !allowToEditFlow || !keywords.length || selectedNumbers.length === 0
              ? '!bg-gray-200 !border-gray-200'
              : '!bg-teal-600 !hover:bg-teal-700 !border-teal-600 !hover:border-teal-700'
          } font-[500]`}
        >
          Save Changes
        </Button>
      </div>
    </div>
  );
}
